/*!
 * YouTube tracking for Snowplow v3.13.1 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd
 * Licensed under BSD-3-Clause
 */

import { dispatchToTrackersInCollection } from '@snowplow/browser-tracker-core';
import { LOG, buildSelfDescribingEvent } from '@snowplow/tracker-core';
import { __assign, __spreadArray } from 'tslib';

// URLSearchParams is not supported in IE
// https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
function addUrlParam(url, key, value) {
  var urlParams = parseUrlParams(url);
  urlParams[key] = value;
  return "".concat(url, "?").concat(urlParamsToString(urlParams));
}
function parseUrlParams(url) {
  var params = {};
  var urlParams = url.split('?')[1];
  if (!urlParams) return params;
  urlParams.split('&').forEach(function (p) {
    var param = p.split('=');
    params[param[0]] = param[1];
  });
  return params;
}
function urlParamsToString(urlParams) {
  // convert an object of url parameters to a string
  var params = '';
  Object.keys(urlParams).forEach(function (p) {
    params += "".concat(p, "=").concat(urlParams[p], "&");
  });
  return params.slice(0, -1);
}
var YouTubeIFrameAPIURL = 'https://www.youtube.com/iframe_api';
// The payload a YouTube player event emits has no identifier of what event it is
// Some payloads can emit the same data
// i.e. onError and onPlaybackRateChange can both emit '{data: 2}'
var YTPlayerEvent = /*#__PURE__*/function (YTPlayerEvent) {
  YTPlayerEvent["ONSTATECHANGE"] = "onStateChange";
  YTPlayerEvent["ONPLAYBACKQUALITYCHANGE"] = "onPlaybackQualityChange";
  YTPlayerEvent["ONERROR"] = "onError";
  YTPlayerEvent["ONAPICHANGE"] = "onApiChange";
  YTPlayerEvent["ONPLAYBACKRATECHANGE"] = "onPlaybackRateChange";
  return YTPlayerEvent;
}(YTPlayerEvent || {});
var YTStateEvent = {
  '-1': 'unstarted',
  '0': 'ended',
  '1': 'play',
  '2': 'pause',
  '3': 'buffering',
  '5': 'cued'
};
var CaptureEventToYouTubeEvent = {
  playbackratechange: YTPlayerEvent.ONPLAYBACKRATECHANGE,
  playbackqualitychange: YTPlayerEvent.ONPLAYBACKQUALITYCHANGE,
  error: YTPlayerEvent.ONERROR,
  apichange: YTPlayerEvent.ONAPICHANGE
};
// As every state event requires YTPlayerEvent.ONSTATECHANGE, they are added
// to CaptureEventToYouTubeEvent with the below loop
Object.keys(YTStateEvent).forEach(function (k) {
  return CaptureEventToYouTubeEvent[YTStateEvent[k]] = YTPlayerEvent.ONSTATECHANGE;
});
var YTState = /*#__PURE__*/function (YTState) {
  YTState["UNSTARTED"] = "unstarted";
  YTState["ENDED"] = "ended";
  YTState["PLAYING"] = "play";
  YTState["PAUSED"] = "pause";
  YTState["BUFFERING"] = "buffering";
  YTState["CUED"] = "cued";
  return YTState;
}(YTState || {});
var YTError = {
  2: 'INVALID_PARAMETER',
  5: 'HTML5_PLAYER_ERROR',
  100: 'NOT_FOUND',
  101: 'EMBED_DISALLOWED',
  150: 'EMBED_DISALLOWED'
};
var SnowplowEvent = /*#__PURE__*/function (SnowplowEvent) {
  SnowplowEvent["PERCENTPROGRESS"] = "percentprogress";
  SnowplowEvent["SEEK"] = "seek";
  SnowplowEvent["VOLUMECHANGE"] = "volumechange";
  return SnowplowEvent;
}(SnowplowEvent || {});
function buildYouTubeEvent(player, eventName, conf, eventData) {
  var data = {
    type: eventName
  };
  if (conf.hasOwnProperty('label')) data.label = conf.label;
  var context = [getYouTubeEntities(player, conf.urlParameters, eventData), getMediaPlayerEntities(eventName, player, conf.urlParameters, eventData)];
  return {
    schema: 'iglu:com.snowplowanalytics.snowplow/media_player_event/jsonschema/1-0-0',
    data: data,
    context: context
  };
}
function getYouTubeEntities(player, urlParameters, eventData) {
  var _a;
  var spherical = player.getSphericalProperties();
  var playerStates = (_a = {}, _a[YT.PlayerState.BUFFERING] = false, _a[YT.PlayerState.CUED] = false, _a[YT.PlayerState.UNSTARTED] = false, _a);
  var state = player.getPlayerState();
  if (state in playerStates) {
    playerStates[state] = true;
  }
  var data = {
    autoPlay: urlParameters.autoplay === '1',
    avaliablePlaybackRates: player.getAvailablePlaybackRates(),
    avaliableQualityLevels: player.getAvailableQualityLevels(),
    buffering: playerStates[YT.PlayerState.BUFFERING],
    controls: urlParameters.controls !== '0',
    cued: playerStates[YT.PlayerState.CUED],
    loaded: parseInt(String(player.getVideoLoadedFraction() * 100)),
    playbackQuality: player.getPlaybackQuality(),
    playerId: player.getIframe().id,
    unstarted: playerStates[YT.PlayerState.UNSTARTED],
    url: player.getVideoUrl()
  };
  if (spherical) data = __assign(__assign({}, data), spherical);
  if (eventData === null || eventData === void 0 ? void 0 : eventData.error) data.error = eventData.error;
  var playlistIndex = player.getPlaylistIndex();
  if (playlistIndex !== -1) data.playlistIndex = playlistIndex;
  var playlist = player.getPlaylist();
  if (playlist) {
    data.playlist = playlist.map(function (item) {
      return parseInt(item);
    });
  }
  return {
    schema: 'iglu:com.youtube/youtube/jsonschema/1-0-0',
    data: data
  };
}
function getMediaPlayerEntities(e, player, urlParameters, eventData) {
  var _a;
  var playerStates = (_a = {}, _a[YT.PlayerState.ENDED] = false, _a[YT.PlayerState.PAUSED] = false, _a);
  var state = player.getPlayerState();
  if (state in playerStates) {
    playerStates[state] = true;
  }
  var data = {
    currentTime: player.getCurrentTime(),
    duration: player.getDuration(),
    ended: playerStates[YT.PlayerState.ENDED],
    loop: urlParameters.loop === '1',
    muted: player.isMuted(),
    paused: playerStates[YT.PlayerState.PAUSED],
    playbackRate: player.getPlaybackRate(),
    volume: Math.round(player.getVolume())
  };
  if (e === SnowplowEvent.PERCENTPROGRESS) {
    data.percentProgress = eventData.percentThrough;
  }
  return {
    schema: 'iglu:com.snowplowanalytics.snowplow/media_player/jsonschema/1-0-0',
    data: data
  };
}
var YTEvent = /*#__PURE__*/function (YTEvent) {
  YTEvent["STATECHANGE"] = "statechange";
  YTEvent["PLAYBACKQUALITYCHANGE"] = "playbackqualitychange";
  YTEvent["ERROR"] = "error";
  YTEvent["APICHANGE"] = "apichange";
  YTEvent["PLAYBACKRATECHANGE"] = "playbackratechange";
  YTEvent["READY"] = "ready";
  return YTEvent;
}(YTEvent || {});
[YTState.BUFFERING, YTState.CUED, YTState.ENDED, YTState.PAUSED, YTState.PLAYING, YTState.UNSTARTED];
var AllEvents = __spreadArray(__spreadArray(__spreadArray([], Object.keys(YTEvent).map(function (k) {
  return YTEvent[k];
}), true), Object.keys(SnowplowEvent).map(function (k) {
  return SnowplowEvent[k];
}), true), Object.keys(YTState).map(function (k) {
  return YTState[k];
}), true);
var DefaultEvents = [YTEvent.READY, YTState.PAUSED, YTState.PLAYING, YTState.ENDED, SnowplowEvent.SEEK, SnowplowEvent.VOLUMECHANGE, YTEvent.PLAYBACKQUALITYCHANGE, YTEvent.PLAYBACKRATECHANGE, SnowplowEvent.PERCENTPROGRESS];
var EventGroups = {
  AllEvents: AllEvents,
  DefaultEvents: DefaultEvents
};

/*
 * Copyright (c) 2022 Snowplow Analytics Ltd
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 *    list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 *    this list of conditions and the following disclaimer in the documentation
 *    and/or other materials provided with the distribution.
 *
 * 3. Neither the name of the copyright holder nor the names of its
 *    contributors may be used to endorse or promote products derived from
 *    this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE
 * FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
 * DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
 * SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
 * OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
 * OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
var _trackers = {};
var trackedPlayers = {};
var trackingQueue = [];
function YouTubeTrackingPlugin() {
  return {
    activateBrowserPlugin: function (tracker) {
      _trackers[tracker.id] = tracker;
    }
  };
}
function trackEvent(event, trackers) {
  if (trackers === void 0) {
    trackers = Object.keys(_trackers);
  }
  dispatchToTrackersInCollection(trackers, _trackers, function (t) {
    t.core.track(buildSelfDescribingEvent({
      event: event
    }), event.context, event.timestamp);
  });
}
function trackingOptionsParser(id, conf) {
  var player = typeof id === 'string' ? undefined : id;
  var elementId = typeof id === 'string' ? id : id.getIframe().id;
  var defaultBoundaries = [10, 25, 50, 75];
  var defaultUpdateRate = 500;
  var parsed = {
    mediaId: elementId,
    player: player,
    captureEvents: (conf === null || conf === void 0 ? void 0 : conf.captureEvents) || DefaultEvents,
    youtubeEvents: [],
    updateRate: (conf === null || conf === void 0 ? void 0 : conf.updateRate) || defaultUpdateRate
  };
  if (conf === null || conf === void 0 ? void 0 : conf.label) parsed.label = conf.label;
  var parsedEvents = [];
  for (var _i = 0, _a = parsed.captureEvents; _i < _a.length; _i++) {
    var ev = _a[_i];
    // If an event is an EventGroup, get the events from that group
    if (EventGroups.hasOwnProperty(ev)) {
      parsedEvents = parsedEvents.concat(EventGroups[ev]);
    } else if (AllEvents.indexOf(ev) === -1) {
      LOG.warn("'".concat(ev, "' is not a valid event"));
    } else {
      parsedEvents.push(ev);
    }
  }
  parsed.captureEvents = parsedEvents;
  // Check if any events in `parsed.captureEvents` require a listener on a `YTPlayerEvent`
  // for them to fire
  for (var _b = 0, _c = parsed.captureEvents; _b < _c.length; _b++) {
    var ev = _c[_b];
    if (CaptureEventToYouTubeEvent.hasOwnProperty(ev) && parsed.youtubeEvents.indexOf(CaptureEventToYouTubeEvent[ev]) === -1) {
      parsed.youtubeEvents.push(CaptureEventToYouTubeEvent[ev]);
    }
  }
  // If "percentprogress" is in `captureEvents`, add the `progress` object
  // to the tracking options with the user-defined boundaries, or the default
  if (parsed.captureEvents.indexOf(SnowplowEvent.PERCENTPROGRESS) !== -1) {
    parsed.progress = {
      boundaries: (conf === null || conf === void 0 ? void 0 : conf.boundaries) || defaultBoundaries,
      boundaryTimeoutIds: []
    };
    // We need to monitor state changes for 'percentprogress' events
    // as `progressHandler` uses these to set/reset timeouts
    if (parsed.youtubeEvents.indexOf(YTPlayerEvent.ONSTATECHANGE) === -1) {
      parsed.youtubeEvents.push(YTPlayerEvent.ONSTATECHANGE);
    }
  }
  return parsed;
}
function enableYouTubeTracking(args) {
  if (!Object.keys(_trackers).length) {
    LOG.error('Check YoutubeTrackingPlugin is initialized in tracker config');
    return;
  }
  var conf = trackingOptionsParser(args.id, args.options);
  if (typeof args.id !== 'string') {
    conf.urlParameters = parseUrlParams(args.id.getIframe().src);
    addExistingPlayer(conf);
    return;
  }
  var el = document.getElementById(args.id);
  if (!el) {
    LOG.error('Cannot find YouTube iframe');
    return;
  }
  // The 'enablejsapi' parameter is required to be '1' for the API to be able to communicate with the player
  if (el.src.indexOf('enablejsapi') === -1) {
    el.src = addUrlParam(el.src, 'enablejsapi', '1');
  }
  conf.urlParameters = parseUrlParams(el.src);
  // If the API is ready, we can immediately add the listeners
  if (typeof YT !== 'undefined' && typeof YT.Player !== 'undefined') {
    initialisePlayer(conf);
  } else {
    // If not, we put them into a queue that will have listeners added once the API is ready
    // and start trying to load the iframe API
    trackingQueue.push(conf);
    handleYouTubeIframeAPI();
  }
}
var iframeAPIRetryWait = 100;
function handleYouTubeIframeAPI(conf) {
  // First we check if the script tag exists in the DOM, and enable the API if not
  var scriptTags = Array.prototype.slice.call(document.getElementsByTagName('script'));
  if (!scriptTags.some(function (s) {
    return s.src === YouTubeIFrameAPIURL;
  })) {
    // Load the Iframe API
    // https://developers.google.com/youtube/iframe_api_reference
    var tag = document.createElement('script');
    tag.src = YouTubeIFrameAPIURL;
    var firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }
  // Once the API is ready to use, 'YT.Player' will be defined
  // 'YT.Player' is not available immediately after 'YT' is defined,
  // so we need to wait until 'YT' is defined to then check 'YT.Player'
  if (typeof YT === 'undefined' || typeof YT.Player === 'undefined') {
    if (iframeAPIRetryWait <= 6400) {
      setTimeout(function () {
        handleYouTubeIframeAPI();
      }, iframeAPIRetryWait);
      iframeAPIRetryWait *= 2;
    } else {
      LOG.error('YouTube iframe API failed to load');
    }
  } else {
    // Once the API is avaliable, listeners are attached to anything sitting in the queue
    while (trackingQueue.length) {
      initialisePlayer(trackingQueue.pop());
    }
  }
}
/**
 * Adds a player to the list of tracked players, and attaches listeners to the player
 * This is used when a user passes in an existing player instance
 *
 * @param conf - The configuration for the player
 */
function addExistingPlayer(conf) {
  var _a, _b;
  trackedPlayers[conf.mediaId] = {
    player: conf.player,
    conf: conf,
    seekTracking: {
      prevTime: ((_a = conf.player) === null || _a === void 0 ? void 0 : _a.getCurrentTime()) || 0,
      enabled: false
    },
    volumeTracking: {
      prevVolume: ((_b = conf.player) === null || _b === void 0 ? void 0 : _b.getVolume()) || 0,
      enabled: false
    }
  };
  // The 'ready' event is required for modelling purposes, the Out-The-Box YouTube modelling won't work without it
  // Ensure you have 'ready' in your 'captureEvents' array if you are using the Out-The-Box YouTube modelling
  if (conf.captureEvents.indexOf(YTEvent.READY) !== -1) {
    // We need to manually trigger the 'ready' event, as it won't be fired by the player when
    // an existing player is passed in, since it's already been fired
    var readyEvent = buildYouTubeEvent(conf.player, 'ready', conf);
    trackEvent(readyEvent);
  }
  attachListeners(conf.player, conf);
}
/**
 * Adds a player to the list of tracked players, and sets up the `onReady` callback to attach listeners once the player is ready
 *
 * @param conf - The configuration for the player
 */
function initialisePlayer(conf) {
  trackedPlayers[conf.mediaId] = {
    player: new YT.Player(conf.mediaId, {
      events: {
        onReady: function (e) {
          return playerReady(e, conf);
        }
      }
    }),
    conf: conf,
    seekTracking: {
      prevTime: 0,
      enabled: false
    },
    volumeTracking: {
      prevVolume: 0,
      enabled: false
    }
  };
}
function attachListeners(player, conf) {
  var _a;
  var builtInEvents = (_a = {}, _a[YTPlayerEvent.ONSTATECHANGE] = function (e) {
    var stateEventInCaptureEvents = conf.captureEvents.indexOf(YTStateEvent[e.data.toString()]) !== -1;
    // If the user is tracking 'percentprogress' events, we need to pass these state changes to `progressHandler`,
    // even if the user is not tracking the state changes themselves
    if (conf.captureEvents.indexOf(SnowplowEvent.PERCENTPROGRESS) !== -1) {
      // Only pass the event to `progressHandler` if the event isn't in the `captureEvents` array
      // As otherwise it will be handled by `youtubeEvent` below
      if (!stateEventInCaptureEvents) {
        progressHandler(player, YTStateEvent[e.data], conf);
      }
    }
    if (stateEventInCaptureEvents) {
      youtubeEvent(trackedPlayers[conf.mediaId].player, YTStateEvent[e.data], conf);
    }
  }, _a[YTPlayerEvent.ONPLAYBACKQUALITYCHANGE] = function () {
    return youtubeEvent(trackedPlayers[conf.mediaId].player, YTEvent.PLAYBACKQUALITYCHANGE, conf);
  }, _a[YTPlayerEvent.ONAPICHANGE] = function () {
    return youtubeEvent(trackedPlayers[conf.mediaId].player, YTEvent.APICHANGE, conf);
  }, _a[YTPlayerEvent.ONERROR] = function (e) {
    return youtubeEvent(trackedPlayers[conf.mediaId].player, YTEvent.ERROR, conf, {
      error: YTError[e.data]
    });
  }, _a[YTPlayerEvent.ONPLAYBACKRATECHANGE] = function () {
    return youtubeEvent(trackedPlayers[conf.mediaId].player, YTEvent.PLAYBACKRATECHANGE, conf);
  }, _a);
  conf.youtubeEvents.forEach(function (youtubeEventName) {
    player.addEventListener(youtubeEventName, function (eventData) {
      return builtInEvents[youtubeEventName](eventData);
    });
  });
}
/**
 * Callback for when the player is ready
 *
 * This is used when a user passes in the string id of an IFrame player
 *
 * Attaches the listeners to the player and fires the captured `onReady` event
 *
 * @param event - The event object from the YouTube API (in this case, onReady)
 * @param conf - The configuration object for the player
 */
function playerReady(event, conf) {
  var player = event.target;
  attachListeners(player, conf);
  if (conf.captureEvents.indexOf(YTEvent.READY) !== -1) {
    youtubeEvent(player, YTEvent.READY, conf);
  }
}
function youtubeEvent(player, eventName, conf, eventData) {
  var playerInstance = trackedPlayers[conf.mediaId];
  if (!playerInstance.seekTracking.enabled && conf.captureEvents.indexOf(SnowplowEvent.SEEK) !== -1) {
    enableSeekTracking(player, conf, eventData);
  }
  if (!playerInstance.volumeTracking.enabled && conf.captureEvents.indexOf(SnowplowEvent.VOLUMECHANGE) !== -1) {
    enableVolumeTracking(player, conf, eventData);
  }
  if (conf.captureEvents.indexOf(SnowplowEvent.PERCENTPROGRESS) !== -1) {
    progressHandler(player, eventName, conf);
  }
  var event = buildYouTubeEvent(player, eventName, conf, eventData);
  trackEvent(event);
}
// Progress Tracking
function progressHandler(player, eventName, conf) {
  var timeoutIds = conf.progress.boundaryTimeoutIds;
  if (eventName === YTState.PAUSED) {
    timeoutIds.forEach(function (id) {
      return clearTimeout(id);
    });
    timeoutIds.length = 0;
  }
  if (eventName === YTState.PLAYING) {
    setPercentageBoundTimeouts(player, conf);
  }
}
function setPercentageBoundTimeouts(player, conf) {
  var _a;
  (_a = conf.progress) === null || _a === void 0 ? void 0 : _a.boundaries.forEach(function (boundary) {
    var absoluteBoundaryTimeMs = player.getDuration() * (boundary / 100) * 1000;
    var timeUntilBoundaryEvent = absoluteBoundaryTimeMs - player.getCurrentTime() * 1000;
    if (0 < timeUntilBoundaryEvent) {
      conf.progress.boundaryTimeoutIds.push(setTimeout(function () {
        return waitAnyRemainingTimeAfterTimeout(player, conf, absoluteBoundaryTimeMs, boundary);
      }, timeUntilBoundaryEvent));
    }
  });
}
// The timeout in setPercentageBoundTimeouts fires ~100 - 300ms early
// waitAnyRemainingTimeAfterTimeout ensures the event is fired accurately
function waitAnyRemainingTimeAfterTimeout(player, conf, percentTime, p) {
  if (player.getCurrentTime() * 1000 < percentTime) {
    setTimeout(function () {
      return waitAnyRemainingTimeAfterTimeout(player, conf, percentTime, p);
    }, 10);
  } else {
    youtubeEvent(player, SnowplowEvent.PERCENTPROGRESS, conf, {
      percentThrough: p
    });
  }
}
// Seek Tracking
function enableSeekTracking(player, conf, eventData) {
  trackedPlayers[conf.mediaId].seekTracking.enabled = true;
  setInterval(function () {
    return seekEventTracker(player, conf, eventData);
  }, conf.updateRate);
}
function seekEventTracker(player, conf, eventData) {
  var playerInstance = trackedPlayers[conf.mediaId];
  var playerTime = player.getCurrentTime();
  if (Math.abs(playerTime - (playerInstance.seekTracking.prevTime + 0.5)) > 1) {
    youtubeEvent(player, SnowplowEvent.SEEK, conf, eventData);
  }
  playerInstance.seekTracking.prevTime = playerTime;
}
// Volume Tracking
function enableVolumeTracking(player, conf, eventData) {
  trackedPlayers[conf.mediaId].volumeTracking.enabled = true;
  trackedPlayers[conf.mediaId].volumeTracking.prevVolume = player.getVolume();
  setInterval(function () {
    return volumeEventTracker(player, conf, eventData);
  }, conf.updateRate);
}
function volumeEventTracker(player, conf, eventData) {
  var playerVolumeTracking = trackedPlayers[conf.mediaId].volumeTracking;
  var playerVolume = player.getVolume();
  if (playerVolume !== playerVolumeTracking.prevVolume) {
    youtubeEvent(player, SnowplowEvent.VOLUMECHANGE, conf, eventData);
  }
  playerVolumeTracking.prevVolume = playerVolume;
}
export { YouTubeTrackingPlugin, enableYouTubeTracking, trackingOptionsParser };
